import React, { useMemo } from 'react';
import './ProductPreview.css';
import {
  TableSurface,
  LegTwoPost,
  LegFourPost,
  FrameBeamHorizontal,
  AccessoryDrawer,
  AccessoryPowerStrip
} from './PreviewAssets';
import useDynamicAssets, { getComponentColors } from '../hooks/useDynamicAssets';

// Default colors (fallback when no dynamic assets available)
const DEFAULT_COLORS = {
  aluminum: '#c0c0c0',
  aluminumLight: '#d8d8d8',
  aluminumDark: '#a0a0a0',
  aluminumOutline: '#808080',
  frame: '#888888',
  frameOutline: '#666666'
};

const ProductPreview = ({ configuration, currentStep }) => {
  // Fetch dynamic SVG assets (AI-generated colors from product photos)
  const { dynamicAssets } = useDynamicAssets();

  // Get colors based on frame type, using dynamic colors if available
  const frameColors = useMemo(() => {
    const frameType = configuration.frame_type === '2-leg' ? '2-leg-frame' : '4-leg-frame';
    return getComponentColors(dynamicAssets, frameType, DEFAULT_COLORS);
  }, [dynamicAssets, configuration.frame_type]);

  // Parse dimensions from strings like "30x60"
  const dimensions = useMemo(() => {
    // Default size
    let width = 60;
    let depth = 30;

    if (configuration.table_size) {
      // Expected format "DepthxWidth" e.g. "30x60" or "22x48"
      const parts = configuration.table_size.split('x');
      if (parts.length === 2) {
        depth = parseInt(parts[0], 10);
        width = parseInt(parts[1], 10);
      }
    }
    return { width, depth };
  }, [configuration.table_size]);

  // Determine Frame Type
  const isTwoLeg = configuration.frame_type === '2-leg';

  // Accessories check
  const hasDrawer = configuration.accessories?.includes('drawer-unit');
  const hasPower = configuration.accessories?.includes('power-strip');
  const hasLight = configuration.accessories?.includes('lighting');

  // Determine surface material colors and properties based on selection
  const getSurfaceMaterial = () => {
    const topSurface = configuration.top_surface || '';

    if (topSurface.startsWith('butcher-block')) {
      return {
        type: 'butcher-block',
        topColor: '#d4a574',      // Maple wood tone
        frontColor: '#c9a66b',    // Slightly darker front edge
        edgeColor: '#b8956a',     // Wood edge
        strokeColor: '#8b6914',   // Wood grain outline
        hasWoodGrain: true,
        thickness: 12,            // 1.75" thick appearance
        edgeRadius: 2             // 1/8" eased edge
      };
    } else if (topSurface.startsWith('laminate-125')) {
      return {
        type: 'laminate-125',
        topColor: '#d5d5d5',      // Dove grey
        frontColor: '#c8c8c8',    // Grey front
        edgeColor: '#c8c8c8',     // Bullnose same color
        strokeColor: '#a0a0a0',
        hasWoodGrain: false,
        thickness: 14,            // 1.25" thick with bullnose
        edgeRadius: 6,            // Bullnose rounded edge
        hasBullnose: true
      };
    } else if (topSurface.startsWith('laminate-075')) {
      return {
        type: 'laminate-075',
        topColor: '#d5d5d5',      // Dove grey
        frontColor: '#c8c8c8',    // Grey front
        edgeColor: '#333333',     // Black PVC edge band
        strokeColor: '#a0a0a0',
        hasWoodGrain: false,
        thickness: 8,             // 0.75" thinner
        edgeRadius: 1,
        hasPvcEdge: true
      };
    } else if (topSurface.startsWith('esd-laminate')) {
      return {
        type: 'esd-laminate',
        topColor: '#4a4a4a',      // Dark grey/black ESD surface
        frontColor: '#3a3a3a',    // Darker front
        edgeColor: '#333333',     // Black edge
        strokeColor: '#2a2a2a',
        hasWoodGrain: false,
        thickness: 14,
        edgeRadius: 1,
        hasEsdPattern: true
      };
    } else if (topSurface.startsWith('stainless-steel')) {
      return {
        type: 'stainless-steel',
        topColor: '#e8e8e8',      // Brushed stainless
        frontColor: '#d0d0d0',    // Metal front
        edgeColor: '#c0c0c0',     // Metal edge
        strokeColor: '#a8a8a8',
        hasWoodGrain: false,
        thickness: 10,
        edgeRadius: 1,
        hasBrushedMetal: true
      };
    } else {
      // Default/No top - simple grey placeholder
      return {
        type: 'default',
        topColor: '#e8e8e8',
        frontColor: '#e0e0e0',
        edgeColor: '#d0d0d0',
        strokeColor: '#999999',
        hasWoodGrain: false,
        thickness: 10,
        edgeRadius: 1
      };
    }
  };

  const surfaceMaterial = getSurfaceMaterial();

  const scale = 4; // visual scaler
  const legHeight = 30; // standard height for visualization
  const svgWidth = (dimensions.width * scale) + 150; // Increased padding for feet
  const svgHeight = (legHeight * scale) + 100;

  const tableY = 50;
  const floorY = tableY + (legHeight * scale);
  const centerX = svgWidth / 2;
  const tableWidthPx = dimensions.width * scale;
  const tableDepthPx = dimensions.depth * scale; // Visual depth for foot size

  // For 2-leg frames, ensure minimum visual separation between legs for proper side-view perspective
  // This affects both leg positioning AND surface width for visual consistency
  const minLegSpacing = 180; // Minimum pixel spacing between leg centers
  const effectiveLegSpread = isTwoLeg ? Math.max(tableWidthPx - 100, minLegSpacing) : 0;

  // Surface should always have overhang beyond the frame
  // Use the larger of actual table width or leg spread, then add overhang
  const overhang = 40; // pixels of overhang on each side combined
  const effectiveSurfaceWidth = isTwoLeg
    ? Math.max(tableWidthPx, effectiveLegSpread) + overhang
    : tableWidthPx + 30; // 4-leg also gets slight overhang


  return (
    <div className="product-preview">
      <div className="preview-container">
        <div className="preview-image">
          <svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} xmlns="http://www.w3.org/2000/svg">
            {/* Floor Shadow */}
            <ellipse cx={centerX} cy={floorY} rx={tableWidthPx / 2 + 40} ry={20} fill="#f0f0f0" />

            {/* LEGS */}
            <g transform={`translate(${centerX}, ${tableY})`}>
              {isTwoLeg ? (
                <>
                  {/* 2-Leg Frame (NXC Series) */}
                  <g transform={`translate(${-effectiveLegSpread / 2}, 0)`}>
                    <LegTwoPost height={legHeight * scale} tableDepth={tableDepthPx} colors={frameColors} />
                  </g>
                  <g transform={`translate(${effectiveLegSpread / 2}, 0)`}>
                    <LegTwoPost height={legHeight * scale} tableDepth={tableDepthPx} colors={frameColors} />
                  </g>

                  {/* Crossbar (Support Beam) - Recessed slightly */}
                  {/* Using a styled rect for the beam connecting the legs */}
                  <rect
                    x={-effectiveLegSpread / 2 + 4}
                    y={10}
                    width={effectiveLegSpread - 8}
                    height={25}
                    fill={frameColors.frame}
                    stroke={frameColors.frameOutline}
                    strokeWidth="1"
                  />
                  {/* Beam detailing */}
                  <line x1={-effectiveLegSpread / 2 + 4} y1={18} x2={effectiveLegSpread / 2 - 4} y2={18} stroke={frameColors.frameOutline} strokeWidth="1" />
                  <line x1={-effectiveLegSpread / 2 + 4} y1={26} x2={effectiveLegSpread / 2 - 4} y2={26} stroke={frameColors.frameOutline} strokeWidth="1" />
                </>
              ) : (
                <>
                  {/* 4 Leg Frame (Levitech Series) - Open front design */}

                  {/* ===== LOWER CROSSBARS (drawn first, behind legs) ===== */}
                  {/* These connect the lower portion of legs, not at top */}

                  {/* Rear crossbar (connects back legs at lower height) */}
                  <rect
                    x={-tableWidthPx / 2 + 15}
                    y={legHeight * scale * 0.4}
                    width={tableWidthPx - 30}
                    height={5}
                    fill={frameColors.frame}
                    stroke={frameColors.frameOutline}
                    strokeWidth="1"
                    opacity="0.85"
                  />

                  {/* ===== BACK LEGS (drawn second, behind front elements) ===== */}
                  <g transform={`translate(${-tableWidthPx / 2 + 15}, -20)`} opacity="0.85">
                    <LegFourPost height={legHeight * scale} colors={frameColors} />
                  </g>
                  <g transform={`translate(${tableWidthPx / 2 - 15}, -20)`} opacity="0.85">
                    <LegFourPost height={legHeight * scale} colors={frameColors} />
                  </g>

                  {/* ===== TOP PERIMETER FRAME ===== */}
                  {/* Back frame beam */}
                  <g transform="translate(0, -20)">
                    <FrameBeamHorizontal width={tableWidthPx - 30} colors={frameColors} />
                  </g>

                  {/* Side frame beams (connect front to back at top) */}
                  <path
                    d={`M${-tableWidthPx / 2 + 15} 0 L${-tableWidthPx / 2 + 15} -20`}
                    stroke={frameColors.frame}
                    strokeWidth="6"
                  />
                  <path
                    d={`M${tableWidthPx / 2 - 15} 0 L${tableWidthPx / 2 - 15} -20`}
                    stroke={frameColors.frame}
                    strokeWidth="6"
                  />

                  {/* ===== SIDE CROSSBARS (perspective lines at lower height) ===== */}
                  {/* Left side lower crossbar */}
                  <path
                    d={`M${-tableWidthPx / 2 + 15} ${legHeight * scale * 0.4 + 2.5} L${-tableWidthPx / 2 + 15} ${legHeight * scale * 0.4 - 17.5}`}
                    stroke={frameColors.frame}
                    strokeWidth="5"
                    opacity="0.9"
                  />
                  {/* Right side lower crossbar */}
                  <path
                    d={`M${tableWidthPx / 2 - 15} ${legHeight * scale * 0.4 + 2.5} L${tableWidthPx / 2 - 15} ${legHeight * scale * 0.4 - 17.5}`}
                    stroke={frameColors.frame}
                    strokeWidth="5"
                    opacity="0.9"
                  />

                  {/* ===== FRONT LEGS (drawn last, in front) ===== */}
                  <g transform={`translate(${-tableWidthPx / 2 + 15}, 0)`}>
                    <LegFourPost height={legHeight * scale} colors={frameColors} />
                  </g>
                  <g transform={`translate(${tableWidthPx / 2 - 15}, 0)`}>
                    <LegFourPost height={legHeight * scale} colors={frameColors} />
                  </g>

                  {/* Front frame beam (at top only, NO lower crossbar - open front) */}
                  <FrameBeamHorizontal width={tableWidthPx - 30} colors={frameColors} />
                </>

              )}
            </g>

            {/* SURFACE */}
            {/* Draw surface "on top" of legs - uses effective width for 2-leg consistency */}
            <g transform={`translate(${centerX}, ${tableY})`}>
              {/* Front edge of surface */}
              <rect
                x={-effectiveSurfaceWidth / 2}
                y={-surfaceMaterial.thickness}
                width={effectiveSurfaceWidth}
                height={surfaceMaterial.thickness}
                rx={surfaceMaterial.edgeRadius}
                fill={surfaceMaterial.frontColor}
                stroke={surfaceMaterial.strokeColor}
                strokeWidth="1"
              />

              {/* PVC Edge band for 0.75" laminate */}
              {surfaceMaterial.hasPvcEdge && (
                <rect
                  x={-effectiveSurfaceWidth / 2}
                  y={-2}
                  width={effectiveSurfaceWidth}
                  height={2}
                  fill={surfaceMaterial.edgeColor}
                />
              )}

              {/* Bullnose edge highlight for 1.25" laminate */}
              {surfaceMaterial.hasBullnose && (
                <>
                  <ellipse
                    cx={-effectiveSurfaceWidth / 2 + 3}
                    cy={-surfaceMaterial.thickness / 2}
                    rx={3}
                    ry={surfaceMaterial.thickness / 2 - 1}
                    fill={surfaceMaterial.edgeColor}
                    stroke={surfaceMaterial.strokeColor}
                    strokeWidth="0.5"
                  />
                  <ellipse
                    cx={effectiveSurfaceWidth / 2 - 3}
                    cy={-surfaceMaterial.thickness / 2}
                    rx={3}
                    ry={surfaceMaterial.thickness / 2 - 1}
                    fill={surfaceMaterial.edgeColor}
                    stroke={surfaceMaterial.strokeColor}
                    strokeWidth="0.5"
                  />
                </>
              )}

              {/* Surface Top Perspective */}
              <path
                d={`M${-effectiveSurfaceWidth / 2} ${-surfaceMaterial.thickness} L${-effectiveSurfaceWidth / 2 + 20} ${-surfaceMaterial.thickness - 20} L${effectiveSurfaceWidth / 2 - 20} ${-surfaceMaterial.thickness - 20} L${effectiveSurfaceWidth / 2} ${-surfaceMaterial.thickness} Z`}
                fill={surfaceMaterial.topColor}
                stroke={surfaceMaterial.strokeColor}
                strokeWidth="0.5"
              />

              {/* Wood grain pattern for butcher block */}
              {surfaceMaterial.hasWoodGrain && (
                <g opacity="0.4">
                  {/* Vertical grain lines on top surface */}
                  {Array.from({ length: Math.floor(effectiveSurfaceWidth / 15) }).map((_, i) => (
                    <line
                      key={`grain-${i}`}
                      x1={-effectiveSurfaceWidth / 2 + 20 + i * 15}
                      y1={-surfaceMaterial.thickness - 18}
                      x2={-effectiveSurfaceWidth / 2 + i * 15}
                      y2={-surfaceMaterial.thickness}
                      stroke="#a67c52"
                      strokeWidth="0.8"
                    />
                  ))}
                  {/* Grain lines on front edge */}
                  {Array.from({ length: Math.floor(effectiveSurfaceWidth / 20) }).map((_, i) => (
                    <line
                      key={`front-grain-${i}`}
                      x1={-effectiveSurfaceWidth / 2 + 10 + i * 20}
                      y1={-surfaceMaterial.thickness + 2}
                      x2={-effectiveSurfaceWidth / 2 + 10 + i * 20}
                      y2={-2}
                      stroke="#a67c52"
                      strokeWidth="0.5"
                    />
                  ))}
                </g>
              )}

              {/* ESD conductive grid pattern */}
              {surfaceMaterial.hasEsdPattern && (
                <g opacity="0.15">
                  {Array.from({ length: Math.floor(effectiveSurfaceWidth / 30) }).map((_, i) => (
                    <line
                      key={`esd-${i}`}
                      x1={-effectiveSurfaceWidth / 2 + 20 + i * 30}
                      y1={-surfaceMaterial.thickness - 18}
                      x2={-effectiveSurfaceWidth / 2 + i * 30}
                      y2={-surfaceMaterial.thickness}
                      stroke="#6a6a6a"
                      strokeWidth="1"
                      strokeDasharray="4,4"
                    />
                  ))}
                </g>
              )}

              {/* Brushed metal texture for stainless steel */}
              {surfaceMaterial.hasBrushedMetal && (
                <g opacity="0.2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <line
                      key={`brush-${i}`}
                      x1={-effectiveSurfaceWidth / 2 + 22}
                      y1={-surfaceMaterial.thickness - 16 + i * 3}
                      x2={effectiveSurfaceWidth / 2 - 22}
                      y2={-surfaceMaterial.thickness - 16 + i * 3}
                      stroke="#ffffff"
                      strokeWidth="1"
                    />
                  ))}
                </g>
              )}
            </g>

            {/* ACCESSORIES */}
            <g transform={`translate(${centerX}, ${tableY})`}>
              {hasDrawer && (
                <g transform={`translate(${-tableWidthPx / 4}, 0)`}>
                  <AccessoryDrawer x={0} y={0} />
                </g>
              )}
              {hasPower && (
                <g transform={`translate(0, -10)`}>
                  <AccessoryPowerStrip width={60} />
                </g>
              )}
              {hasLight && (
                <g transform={`translate(0, -100)`}>
                  {/* Overhead light structure */}
                  <path
                    d={`M${-tableWidthPx / 2 + 10} 90 L${-tableWidthPx / 2 + 10} -50 L${tableWidthPx / 2 - 10} -50 L${tableWidthPx / 2 - 10} 90`}
                    fill="none"
                    stroke="#666"
                    strokeWidth="4"
                  />
                  <rect
                    x={-tableWidthPx / 3}
                    y={-60}
                    width={tableWidthPx / 1.5}
                    height={10}
                    fill="#ffeb3b"
                    stroke="#999"
                  />
                </g>
              )}
            </g>

          </svg>
        </div>
        <div className="preview-dimensions">
          <span>Width: {dimensions.width}"</span>
          <span>Depth: {dimensions.depth}"</span>
        </div>
      </div>
    </div>
  );
};

export default ProductPreview;
